import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoicesApi, currencySymbols, formatInvoiceDate } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Download, Pencil, Loader2 } from 'lucide-react';
import html2pdf from 'html2pdf.js';

const InvoiceViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const invoiceRef = useRef(null);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await invoicesApi.getFull(id);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoiceRef.current) return;
    
    setDownloading(true);
    try {
      const element = invoiceRef.current;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${invoice.invoice_number.replace(/\//g, '-')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false,
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { mode: 'avoid-all' }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!data) return null;

  const { invoice, client, bank_account, company } = data;
  const currencySymbol = currencySymbols[invoice.currency] || '$';

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 no-print">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/invoices')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-zinc-900">
              Invoice {invoice.invoice_number}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/invoices/${id}/edit`)}
            data-testid="edit-invoice-btn"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button 
            className="bg-orange-500 hover:bg-orange-600"
            onClick={handleDownloadPDF}
            disabled={downloading}
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Invoice Preview - Optimized for single A4 page */}
      <div className="flex justify-center">
        <div 
          ref={invoiceRef}
          style={{ 
            width: '190mm',
            minHeight: '277mm',
            maxHeight: '277mm',
            backgroundColor: 'white',
            fontFamily: 'Helvetica, Arial, sans-serif',
            padding: '8mm',
            boxSizing: 'border-box',
          }}
          className="shadow-xl"
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3mm' }}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#18181B', letterSpacing: '-0.5px' }}>
              TAX INVOICE
            </span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#18181B' }}>
              {company?.company_name || 'Company'}
            </span>
          </div>

          {/* Gradient bar */}
          <div style={{ 
            height: '4px', 
            background: 'linear-gradient(to right, #EA580C, #F59E0B, #10B981)', 
            marginBottom: '6mm',
            borderRadius: '2px'
          }}></div>

          {/* Company & Invoice Details */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6mm' }}>
            <div style={{ maxWidth: '55%' }}>
              <p style={{ fontSize: '12px', fontWeight: '700', color: '#18181B', marginBottom: '2mm' }}>
                {company?.company_name}
              </p>
              <p style={{ fontSize: '9px', color: '#52525B', lineHeight: '1.4', marginBottom: '2mm', whiteSpace: 'pre-line' }}>
                {company?.company_address}
              </p>
              {company?.gstin && (
                <p style={{ fontSize: '9px', color: '#52525B' }}>
                  <span style={{ fontWeight: '600' }}>GSTIN:</span> {company.gstin}
                </p>
              )}
              {company?.contact && (
                <p style={{ fontSize: '9px', color: '#52525B' }}>
                  <span style={{ fontWeight: '600' }}>Contact:</span> {company.contact}
                </p>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '8px', color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '1mm' }}>
                Invoice #
              </p>
              <p style={{ fontSize: '12px', fontWeight: '700', color: '#18181B', fontFamily: 'monospace', marginBottom: '4mm' }}>
                {invoice.invoice_number}
              </p>
              <p style={{ fontSize: '8px', color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '1mm' }}>
                Invoice Date
              </p>
              <p style={{ fontSize: '11px', color: '#18181B', fontFamily: 'monospace' }}>
                {formatInvoiceDate(invoice.invoice_date, client?.region)}
              </p>
            </div>
          </div>

          {/* Bill To */}
          <div style={{ marginBottom: '5mm' }}>
            <p style={{ fontSize: '8px', color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '2mm' }}>
              Bill To
            </p>
            <p style={{ fontSize: '12px', fontWeight: '700', color: '#18181B', marginBottom: '1mm' }}>
              {client?.name}
            </p>
            <p style={{ fontSize: '9px', color: '#52525B', whiteSpace: 'pre-line' }}>
              {client?.address}
            </p>
          </div>

          {/* Items Table */}
          <div style={{ marginBottom: '4mm' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #18181B' }}>
                  <th style={{ textAlign: 'left', fontSize: '8px', fontWeight: '700', color: '#18181B', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '2mm 0' }}>
                    Description
                  </th>
                  <th style={{ textAlign: 'right', fontSize: '8px', fontWeight: '700', color: '#18181B', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '2mm 0' }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #E4E4E7' }}>
                    <td style={{ fontSize: '10px', color: '#3F3F46', padding: '3mm 0' }}>
                      {item.description}
                    </td>
                    <td style={{ fontSize: '10px', color: '#18181B', fontFamily: 'monospace', textAlign: 'right', padding: '3mm 0' }}>
                      {currencySymbol}{item.amount.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '5mm' }}>
            <div style={{ width: '50mm' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid #18181B', paddingTop: '3mm' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#18181B' }}>TOTAL</span>
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#18181B', fontFamily: 'monospace' }}>
                  {currencySymbol}{invoice.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          {bank_account && (
            <div style={{ backgroundColor: '#FAFAFA', borderRadius: '4px', padding: '4mm', marginBottom: '4mm' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3mm' }}>
                <span style={{ fontSize: '8px', color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700' }}>
                  Payment Details
                </span>
                {bank_account.payment_method && (
                  <span style={{ fontSize: '7px', backgroundColor: '#E4E4E7', color: '#3F3F46', padding: '1mm 2mm', borderRadius: '2px', fontWeight: '600' }}>
                    {bank_account.payment_method}
                  </span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3mm' }}>
                <div>
                  <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Payee Name</p>
                  <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B' }}>{bank_account.account_name}</p>
                </div>
                <div>
                  <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Bank Name</p>
                  <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B' }}>{bank_account.bank_name}</p>
                </div>
                <div>
                  <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Account Number</p>
                  <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.account_number}</p>
                </div>
                {bank_account.routing_number && (
                  <div>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>ACH Routing Number</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.routing_number}</p>
                  </div>
                )}
                {bank_account.fedwire_routing && (
                  <div>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Fedwire Routing Number</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.fedwire_routing}</p>
                  </div>
                )}
                {bank_account.sort_code && (
                  <div>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Sort Code</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.sort_code}</p>
                  </div>
                )}
                {bank_account.ifsc_code && (
                  <div>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>IFSC Code (Local)</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.ifsc_code}</p>
                  </div>
                )}
                {bank_account.swift_code && (
                  <div>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>SWIFT Code (International)</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.swift_code}</p>
                  </div>
                )}
                {bank_account.iban && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>IBAN</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.iban}</p>
                  </div>
                )}
                {bank_account.institution_number && (
                  <div>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Institution Number</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.institution_number}</p>
                  </div>
                )}
                {bank_account.transit_number && (
                  <div>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Transit Number</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B', fontFamily: 'monospace' }}>{bank_account.transit_number}</p>
                  </div>
                )}
                {bank_account.address && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <p style={{ fontSize: '7px', color: '#71717A', marginBottom: '1mm' }}>Beneficiary Address</p>
                    <p style={{ fontSize: '9px', fontWeight: '600', color: '#18181B' }}>{bank_account.address}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div style={{ marginBottom: '4mm', paddingTop: '3mm', borderTop: '1px solid #E4E4E7' }}>
              <p style={{ fontSize: '8px', color: '#71717A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2mm' }}>Notes</p>
              <p style={{ fontSize: '9px', color: '#52525B' }}>{invoice.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div style={{ marginTop: 'auto' }}>
            <div style={{ 
              height: '4px', 
              background: 'linear-gradient(to right, #EA580C, #F59E0B, #10B981)', 
              borderRadius: '2px',
              marginBottom: '3mm'
            }}></div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '9px', color: '#52525B', marginBottom: '1mm' }}>Thank you for your business!</p>
              <p style={{ fontSize: '7px', color: '#A1A1AA' }}>Please use invoice number as payment reference.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceViewPage;
